import React, { ChangeEvent, useEffect, useState } from "react";
import { Card } from "../../../components/card/Card";
import Button from "../../../components/button/Button";
import { HiEllipsisVertical, HiPlus } from "react-icons/hi2";
import {
  useAddTagMutation,
  useDeleteTagMutation,
  useGetTagsQuery,
  useUpdateTagMutation,
} from "../../../slices/ContactTagsSlice";
import { getLinkedFirm } from "../../../sessionStorage/sessionStorage";
import { LottieLoading } from "../../../components/graphics/LottieLoading";
import { Dropdown } from "../../../components/dropdown/new-dropdown/Dropdown";
import moment from "moment";
import classNames from "classnames";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { EditPencilSvg } from "../../../components/svg/EditPencilSvg";
import { TrashSvg } from "../../../components/svg/TrashSvg";
import { FaTrashCan } from "react-icons/fa6";
import { DeactivateSvg } from "../../../components/svg/DeactivateSvg";
import { SettingsIconButton } from "../../../components/button/SettingsIconButton";
import { DotsSvg } from "../../../components/svg/DotsSvg";
import { SettingsDropdown } from "../../../components/dropdown/SettingsDropdown";
import { SettingsConfirmModal } from "../../../components/modal/SettingsConfirmModal";
import { serverErrorMessage } from "../../settings/workTypesV2/model/workTypeEditor.utils";
import { tagCreatorName, tagNameProblem } from "./tags.utils";
import { toast } from "react-toastify";

export const TagsView = () => {
  const { data: tags, isFetching } = useGetTagsQuery({
    orgId: getLinkedFirm()?.orgId || "",
  });
  const [deleteTag, { isLoading: isDeletingTag }] = useDeleteTagMutation();
  const [tagToDelete, setTagToDelete] = useState<{
    id: string;
    tagName: string;
    counts?: number;
  } | null>(null);
  const [updateTag, { isLoading: isUpdating }] = useUpdateTagMutation();
  const [addTag, { isLoading }] = useAddTagMutation();

  const [isOpenAdd, setIsOpenAdd] = useState(false);
  const [tagValue, setTagValue] = useState("");
  const [isOpenUpdate, setIsOpenUpdate] = useState(false);
  const [tagUpdateValue, setTagUpdateValue] = useState("");
  const [tagId, setTagId] = useState("");
  const [tagDescription, setTagDescription] = useState("");
  const [error, setError] = useState("");
  // Messages appear once the person tried to save (or typed too much), not
  // while the empty box is first shown.
  const [triedSave, setTriedSave] = useState(false);

  const problemFor = (value: string, exceptId?: string) =>
    tagNameProblem(value, tags, exceptId);

  const handleAddChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const problem = problemFor(value);
    setError(triedSave || value.trim().length > 30 ? problem : "");
    setTagValue(value);
  };

  const handleUpdateChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setError(problemFor(value, tagId));
    setTagUpdateValue(value);
  };

  const closeAdd = () => {
    setIsOpenAdd(false);
    setError("");
    setTriedSave(false);
    setTagValue("");
    setTagDescription("");
  };

  const closeUpdate = () => {
    setIsOpenUpdate(false);
    setTagUpdateValue("");
    setTagDescription("");
    setError("");
  };

  const saveNew = async () => {
    setTriedSave(true);
    const problem = problemFor(tagValue);
    if (problem) {
      setError(problem);
      return;
    }
    try {
      await addTag({
        tagName: tagValue.trim(),
        orgId: getLinkedFirm()?.orgId,
        description: tagDescription,
      }).unwrap();
      closeAdd();
    } catch (e) {
      setError(serverErrorMessage(e, "The tag couldn't be saved. Please try again."));
    }
  };

  const saveUpdate = async () => {
    const problem = problemFor(tagUpdateValue, tagId);
    if (problem) {
      setError(problem);
      return;
    }
    try {
      await updateTag({
        tagName: tagUpdateValue.trim(),
        id: tagId,
        description: tagDescription,
      }).unwrap();
      closeUpdate();
    } catch (e) {
      setError(serverErrorMessage(e, "The tag couldn't be saved. Please try again."));
    }
  };

  return (
    <div>
      <div className={"flex justify-between py-6"}>
        <div className={"text-[17px] font-extrabold text-[#1F2433]"}>Tags</div>
        <Button
          label={"+ Create Tag"}
          labelClasses={"text-[14px] px-2"}
          size={"custom"}
          extraClasses={"btn-sm px-2"}
          onClick={() => setIsOpenAdd(true)}
        />
      </div>
      <Card extraClasses={"shadow-box rounded-xl"}>
        <div className={"max-w-full"}>
          <div
            className={
              "grid w-full grid-cols-6 items-center border-b border-gray-200 p-4"
            }>
            <div className={"text-[11px] font-bold uppercase tracking-[0.04em] text-[#9097A8]"}>
              Tags
            </div>
            <div
              className={
                "text-center text-[11px] font-bold uppercase tracking-[0.04em] text-[#9097A8]"
              }>
              Description
            </div>
            <div
              className={
                "text-center text-[11px] font-bold uppercase tracking-[0.04em] text-[#9097A8]"
              }>
              Count
            </div>
            <div
              className={
                "text-center text-[11px] font-bold uppercase tracking-[0.04em] text-[#9097A8]"
              }>
              Created By
            </div>
            <div
              className={
                "text-center text-[11px] font-bold uppercase tracking-[0.04em] text-[#9097A8]"
              }>
              Date Created
            </div>
          </div>
          <div>
            {isFetching || isLoading ? (
              <LottieLoading />
            ) : !tags?.length ? (
              <div className={"flex justify-center py-4 font-semibold"}>
                No tags
              </div>
            ) : (
              tags?.map((tag) => (
                <div
                  key={tag.id}
                  className={
                    "mb-1 grid w-full grid-cols-6 items-center justify-between border-b border-gray-300 p-4 text-base"
                  }>
                  <div className={"font-Manrope text-sm font-semibold"}>
                    {tag.tagName}
                  </div>
                  <div
                    className={
                      "flex justify-center text-start font-Manrope text-sm text-gray-500"
                    }>
                    {tag.description}
                  </div>
                  <div
                    className={
                      "text-center font-Manrope text-sm text-gray-500"
                    }>
                    {tag.counts}
                  </div>
                  <div
                    className={
                      "text-center font-Manrope text-sm text-gray-500"
                    }>
                    {tagCreatorName(tag.createdByUser)}
                  </div>
                  <div
                    className={
                      "text-center font-Manrope text-sm text-gray-500"
                    }>
                    {moment(tag.createdAt).format("DD MMM, YYYY")}
                  </div>
                  <div className={"flex justify-end"}>
                    <SettingsDropdown
                      actionsList={[
                        {
                          name: "Edit",
                          action: () => {
                            setTagUpdateValue(tag.tagName);
                            setError("");
                            setIsOpenUpdate(true);
                            setTagDescription(tag.description);
                            setTagId(tag.id);
                          },
                          icon: <EditPencilSvg />,
                        },
                        {
                          name: "Delete",
                          action: () => setTagToDelete(tag),
                          icon: <TrashSvg />,
                          extraClasses: "text-[#DC2626]",
                          iconColor: "text-[#DC2626]",
                        },
                      ]}>
                      <SettingsIconButton
                        icon={<DotsSvg />}
                        onClick={() => null}
                      />
                    </SettingsDropdown>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </Card>
      {tagToDelete && (
        <SettingsConfirmModal
          onClose={() => setTagToDelete(null)}
          onConfirm={async () => {
            try {
              await deleteTag({
                tagName: tagToDelete.tagName,
                id: tagToDelete.id,
              }).unwrap();
              setTagToDelete(null);
            } catch (e) {
              // QA batch 16 (SET-045): a failed delete used to close the window silently.
              toast.error(
                serverErrorMessage(e, "The tag couldn't be deleted. Please try again.")
              );
            }
          }}
          icon={<TrashSvg />}
          title={`Delete ‘${tagToDelete.tagName}’ tag?`}
          description={
            tagToDelete.counts
              ? `Do you want to delete this tag? It is used on ${
                  tagToDelete.counts
                } contact${
                  tagToDelete.counts === 1 ? "" : "s"
                } and will be removed from ${
                  tagToDelete.counts === 1 ? "it" : "them"
                }.`
              : "Do you want to delete this tag?"
          }
          confirmText={isDeletingTag ? "Deleting..." : "Delete"}
          variant={"danger"}
          isLoading={isDeletingTag}
        />
      )}
      {isOpenUpdate && (
        <div className={`modal ${isOpenUpdate ? "modal-open" : "modal-close"}`}>
          <div className={"modal-box w-[350px] p-0"}>
            <div
              className={
                "border-b border-gray-200 px-3 py-2 text-[16px] font-semibold"
              }>
              Update Tag
            </div>
            <div className={"mb-4 mt-3 flex w-full flex-col gap-1 px-3"}>
              <div className={"text-[14px]"}>Tag Name</div>
              <input
                type={"text"}
                className={
                  "input input-bordered h-[32px] w-full px-2 py-1 text-sm"
                }
                onChange={handleUpdateChange}
                value={tagUpdateValue}
              />
              {error && (
                <p className={"text-sm font-semibold text-error"}>{error}</p>
              )}
            </div>
            <div className={"mb-4 flex w-full flex-col px-3"}>
              <div className={"text-[14px]"}>Description (optional)</div>
              <input
                type={"text"}
                className={
                  "input input-bordered h-[32px] w-full px-2 py-1 text-sm"
                }
                maxLength={50}
                value={tagDescription}
                placeholder={"Description"}
                onChange={(e) => setTagDescription(e.target.value)}
              />
            </div>
            <div
              className={
                "mt-4 flex justify-end gap-3 p-3 text-sm font-semibold"
              }>
              <button
                disabled={isUpdating}
                onClick={closeUpdate}
                className={
                  "flex h-[32px] w-[100px] items-center justify-center rounded-lg border border-[#C8D2DE] px-4 py-2 font-Manrope text-sm text-[#253747] disabled:opacity-75"
                }>
                Cancel
              </button>

              <button
                disabled={isUpdating}
                onClick={saveUpdate}
                className={
                  "flex h-[32px] w-[100px] items-center justify-center rounded-lg bg-[#7c66f0] px-4 py-2 font-Manrope text-sm text-white disabled:opacity-75"
                }>
                Update
              </button>
            </div>
          </div>
        </div>
      )}
      {isOpenAdd && (
        <div className={`modal ${isOpenAdd ? "modal-open" : "modal-close"}`}>
          <div className={"modal-box w-[350px] p-0"}>
            <div
              className={
                "border-b border-gray-200 px-3 py-2 text-[16px] font-semibold"
              }>
              Create Tag
            </div>
            <div className={"mb-4 mt-3 flex w-full flex-col gap-1 px-3"}>
              <div className={"text-[14px]"}>Tag Name</div>
              <input
                type={"text"}
                className={
                  "input input-bordered h-[32px] w-full px-2 py-1 text-sm"
                }
                value={tagValue}
                placeholder={"New tag name"}
                onChange={handleAddChange}
              />
              {error && (
                <p className={"text-sm font-semibold text-error"}>{error}</p>
              )}
            </div>
            <div className={"mb-4 flex w-full flex-col px-3"}>
              <div className={"text-[14px]"}>Description (optional)</div>
              <input
                type={"text"}
                className={
                  "input input-bordered h-[32px] w-full px-2 py-1 text-sm"
                }
                maxLength={50}
                value={tagDescription}
                placeholder={"Description"}
                onChange={(e) => setTagDescription(e.target.value)}
              />
            </div>
            <div
              className={
                "mt-4 flex justify-end gap-3 p-3 text-sm font-semibold"
              }>
              <button
                disabled={isLoading}
                onClick={closeAdd}
                className={
                  "flex h-[32px] w-[100px] items-center justify-center rounded-lg border border-[#C8D2DE] px-4 py-2 font-Manrope text-sm text-[#253747] disabled:opacity-75"
                }>
                Cancel
              </button>

              <button
                disabled={isLoading}
                onClick={saveNew}
                className={
                  "flex h-[32px] w-[100px] items-center justify-center rounded-lg bg-[#7c66f0] px-4 py-2 font-Manrope text-sm text-white disabled:opacity-75"
                }>
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
